/**
 * Notifications Component
 *
 * Displays a notification bell with a dropdown list of notifications for the current user.
 * Fetches notifications from a provider and allows marking as read, acknowledging, and opening notifications.
 *
 * Features:
 * - Shows unread notification count
 * - Lists notifications with critical/revoked tags
 * - Allows marking notifications as read or acknowledged
 * - Allows opening/viewing notifications
 * - Exposes a global reload function for cross-page updates
 *
 * To reload notifications from anywhere in the app:
 *   window.reloadNotificationsList();
 *
 * The global reload function is automatically assigned and cleaned up as the component mounts/unmounts.
 *
 * example on how to subscribe to the global notifications component. this should be added to the onInit function
 * subscribe(null, "global-notifications", {
      async getNotifications(authToken: string): Promise<ReadonlyArray<Notification>> {
        const { NotificationActionSampleComponent } = await import("./components/NotificationActionSampleComponent");

        return new Promise((resolve) => {
          setTimeout(() => {
            resolve([
              { id: "0", title: "Demo Basic Notification", description: "This is a basic notification", critical: false, read: false },
              { id: "1", title: "Demo Critical Notification", description: "This is a critical notification", critical: true, read: false },
              {
                id: "2",
                title: "Demo Action Notification",
                description: "This is a notification with an action",
                relatedModuleId: metadata.id,
                critical: false,
                read: false,
                actionsComponent: NotificationActionSampleComponent,
              },
            ]);
          }, 2000);
        });
      },
      async dismissNotifications(authToken: string, ids: ReadonlyArray<string>): Promise<void> {
        return new Promise((resolve) => {
          resolve();
        });
      },
      async markRead(authToken: string, ids: ReadonlyArray<string>): Promise<void> {
        return new Promise((resolve) => {
          resolve();
        });
      },
      async openNotification(authToken: string, id: string): Promise<void> {
        // Stub implementation
        return new Promise((resolve) => {
          resolve();
        });
      },
      async viewAllNotification(authToken: string): Promise<void> {
        // Stub implementation
        return new Promise((resolve) => {
          resolve();
        });
      },
    } as NotificationsApi);
 */

import { ComponentType, MouseEvent, ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import { Button, ButtonAppearance, Buttons, MenuButton, Tag } from "capitalrx-components-2.0/lib/general";
import * as Color from "capitalrx-components-2.0/lib/general/Color";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Bold, Heading, Paragraph, Regular, Semibold } from "capitalrx-components-2.0/lib/typography";
import styled from "styled-components";
import { useAuthToken, useDataChannel } from "../../hooks";


const NotificationButtonAppearance: ButtonAppearance = {
  ...Buttons.Functional.Tertiary.Large,

  padding: {
    horizontal: 8,
    vertical: 10,
  },
  normal: {
    ...Buttons.Functional.Tertiary.Large.normal,
    foregroundColor: Color.White,
    borderColor: Color.Transparent,
  },
  hover: {
    ...Buttons.Functional.Tertiary.Large.hover,
    foregroundColor: Color.Turqoise["100"],
    borderColor: Color.Transparent,
  },
  pressed: {
    ...Buttons.Functional.Tertiary.Large.pressed,
    foregroundColor: Color.White,
    borderColor: Color.Transparent,
  },
};

/** A notification. */
export interface Notification {
  /** The notification ID. */
  readonly id: string;
  /** The ID of the module the notification is related to. */
  readonly relatedModuleId?: string;
  /** The title. */
  readonly title: string;
  /** The timestamp. */
  readonly timestamp?: string;
  /** Notification body, it revoked is true, then this is going to be presented as the revoked reason */
  readonly description: string;
  /** Indicates whether the notification is critical. */
  readonly critical: boolean;
  /** Indicates whether the notification is revoked. */
  readonly revoked?: boolean;
  /** Indicates whether the notification has been read or not. */
  readonly read: boolean;
  /** An optional React component that is placed beside the dismiss link to provide additional actions or behavior. */
  readonly actionsComponent?: ComponentType;
}


/** The notifications API. */
export interface NotificationsApi {
  /**
   * Gets the current set of notifications.
   * @param authToken The authentication token of the user to get notifications for.
   * @returns A promise that returns the notifications.
   */
  getNotifications(authToken: string): Promise<ReadonlyArray<Notification>>;

  /**
   * Dismisses one of more notifications with the specified IDs.
   * @param authToken The authentication token of the user to dismiss a notification for.
   * @param ids The IDs of the notifications to dismiss.
   */
  dismissNotifications(authToken: string, ids: ReadonlyArray<string>): Promise<void>;

  /**
   * Marks one or more notifications with the specified IDs as read.
   * @param authToken The authentication token of the user to mark notifications read for.
   * @param ids The IDs of the notifications to mark read.
   */
  markRead(authToken: string, ids: ReadonlyArray<string>, data: Notification): Promise<void>;

  /**
   * Pass down anything function to the open notification link
   * @param authToken The authentication token of the user to mark notifications read for.
   * @param ids The IDs of the notifications to mark read.
   */
  openNotification(authToken: string, id: string): Promise<void>;

  /**
   * Pass down anything function to the view all notification link
   * @param authToken The authentication token of the user to mark notifications read for.
   */
  viewAllNotification(authToken: string): Promise<void>;
}

/** The notification button container. */
const NotificationButtonContainer = styled.div`
  display: inline-block;
  position: relative;
`;

/** The notification count indicator. */
const NotificationIndicator = styled.div`
  position: absolute;
  background-color: red;
  width: 1rem;
  height: 1rem;
  border-radius: 0.5rem;
  color: white;
  line-height: 1rem;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  font-size: 12px;
  pointer-events: none;
  z-index: 1;
  bottom: 0.35rem;
  left: -0.15rem;
  box-shadow: 0 0 0 0.125rem #254591;
`;

/** The notification menu body. */
const NotificationMenuBody = styled.div`
  padding: 0.5rem;
  padding-bottom: 0.75rem;
`;

/** The notification outer scroll area. */
const NotificationScrollAreaOuter = styled.div`
  position: relative;
  width: calc(368px - 2.5rem);
`;

/** The notification inner scroll area. */
const NotificationScrollAreaInner = styled.div`
  position: relative;
  width: 368px;
  left: -1.25rem;
  overflow-y: auto;
  max-height: calc(100vh - 10rem);
`;

/** The read notifications message. */
const ReadNotificationsMessage = styled.div`
  background-color: ${Color.toString(Color.LightGrey[50])};
  padding: 40px 0.5rem;
  width: 328px;
  text-align: center;
`;

/** The body of a notification. */
const NotificationBody = styled.div<{ $position: string }>`
  padding: 0 1rem;
  .notification-message {
    padding: ${({ $position }) => ($position === "firstItem" ? "0.5rem 0.5rem 1rem" : "1rem 0.5rem")};
    border-bottom: ${({ $position }) => ($position === "lastItem" ? "none" : `solid 1px ${Color.toString(Color.LightGrey[200])}`)};
    a {
      cursor: pointer;
    }
  }
  .notification-message-header {
    justify-content: flex-start;
    gap: 4px;
  }
`;

/** The read indicator of a notification. */
const NotificationUnreadIndicator = styled.div`
  background-color: ${Color.toString(Color.Turqoise[500])};
  width: 8px;
  height: 8px;
  border-radius: 100%;
  align-self: center;
`;

/** The container component for the menu bar notifications. */
export const Notifications = (): ReactNode => {
  const appNotifications = useDataChannel<NotificationsApi>(null, "global-notifications");
  const [authToken] = useAuthToken();
  const [notifications, setNotifications] = useState<ReadonlyArray<Notification>>([]);

  /**
   * notificationProviders
   * Array of all notification providers subscribed to the "global-notifications" channel.
   * Each provider may come from a different module.
   */
  const notificationProviders = appNotifications.map((sub) => sub.data).filter(Boolean);

  /**
   * reloadNotificationsList
   * Fetches notifications from all providers, merges and deduplicates them by id.
   * Exposed globally via window.reloadNotificationsList for cross-page usage.
   */
  const reloadNotificationsList = useCallback(() => {
    if (notificationProviders.length === 0 || !authToken) {
      setNotifications([]);
      return;
    }
    Promise.all(notificationProviders.map((provider) => provider.getNotifications(authToken)))
      .then((results) => {
        // Flatten and deduplicate by id
        const merged = ([] as Notification[]).concat(...results);
        const deduped = Array.from(new Map(merged.map((n) => [n.id, n])).values());
        setNotifications(deduped);
      })
      .catch(() => {});
  }, [notificationProviders, authToken]);

  /**
   * useEffect: Assigns reloadNotificationsList to window and triggers initial load.
   * Cleans up the global assignment on unmount.
   * Only calls reloadNotificationsList when providers or authToken change, preventing infinite API calls.
   */
  useEffect(() => {
    window.reloadNotificationsList = reloadNotificationsList;
    reloadNotificationsList();
    return () => {
      if (window.reloadNotificationsList === reloadNotificationsList) {
        window.reloadNotificationsList = undefined;
      }
    };
  }, [authToken, notificationProviders.length]);


  /**
   * markAsRead
   * Marks a notification as read in local state immediately.
   */
  const markAsRead = (id: string) => {
    setNotifications((current) => current.map((item) => (item.id === id ? { ...item, read: true } : item)));

  };

  /**
   * markAsReadHandler
   * Marks a notification as read in the backend (using the first provider) and updates local state immediately.
   */
  const markAsReadHandler = useCallback(
    (notification: Notification) => {
      if (!authToken) return;
      const provider = notificationProviders[0];
      try {
        provider?.markRead(authToken, [notification.id], notification);
      } finally {
        markAsRead(notification.id);
      }
    },
    [notificationProviders, authToken],
  );

  /**
   * openNotificationHandler
   * Opens a notification (e.g., navigates or marks as opened in backend using the first provider).
   * if this is not critical notification it is also marked as read on local state
   */
  const openNotificationHandler = useCallback(
    async (notification:Notification) => {
      if (!authToken) return;
      const provider = notificationProviders[0];
      try {
        if(!notification.critical) markAsRead(notification.id);
        await provider?.openNotification(authToken, notification. id);
      } catch {
        // Optionally log error or show feedback
      }
    },
    [notificationProviders, authToken],
  );

  /**
   * viewAllNotificationHandler
   * Triggers the provider's viewAllNotification logic (using the first provider).
   */
  const viewAllNotificationHandler = useCallback(async () => {
    if (!authToken) return;
    const provider = notificationProviders[0];
    try {
      await provider?.viewAllNotification(authToken);
    } catch {
      // Optionally log error or show feedback
    }
  }, [notificationProviders, authToken]);

  const unreadCount = useMemo(() => {
    return notifications.filter((notification) => !notification.read).length;
  }, [notifications]);

  const notificationItems = useMemo(() => {
    return notifications.map((notification) => {
      const { id } = notification;
      return {
        notification: {
          ...notification,
        },
        markAsRead: (ev: MouseEvent) => {
          if (authToken) {
            const provider = notificationProviders[0];
            try {
              provider?.markRead(authToken, [id], notification);
            } catch {
              // just eat any exceptions.
            } finally {
              setNotifications((current) => {
                return current.filter((notification) => notification.id !== id);
              });
            }
          }
          ev.preventDefault();
        },
      };
    });
  }, [notifications, notificationProviders, authToken]);

  const getTruncatedDescription = useCallback((description: string, critical: boolean, revoked: boolean | undefined): string => {
    const shouldTruncate = critical || (!critical && !revoked);
    if (!shouldTruncate) return description;
    return description?.length > 200 ? description?.slice(0, 197) + "..." : description;
  }, []);

  if (notificationProviders.length === 0) return null;

  const supportEmail = useMemo(() => {
    const primeRegex = /(.?)prime(?:uat|qa)?-cap-rx\.com(.?)/;
    if (primeRegex.test(location.hostname)) {
      return "primesupport@cap-rx.com";
    } else {
      return "support@cap-rx.com";
    }
  }, []);

  return (
    <div className="notifications">
      <NotificationButtonContainer>
        {unreadCount > 0 && (
          <NotificationIndicator>
            <Bold>{unreadCount}</Bold>
          </NotificationIndicator>
        )}
        <MenuButton appearance={NotificationButtonAppearance} tabIndex={4} icon="bell" caretIcon={false} tooltip="Notifications">
          <MenuButton.ComponentItem>
            <NotificationMenuBody>
              <Stack spacing="medium">
                <Shelf style={{ height: "24px" }}>
                  <Heading>Notifications</Heading>
                  <Shelf.Spacer />
                  {notifications.length > 0 && (
                    <Button appearance="text" onClick={viewAllNotificationHandler}>
                      View All
                    </Button>
                  )}
                </Shelf>
                {notifications.length === 0 && <ReadNotificationsMessage>No new notifications</ReadNotificationsMessage>}
                {notifications.length > 0 && (
                  <NotificationScrollAreaOuter>
                    <NotificationScrollAreaInner>
                      <Stack spacing="small">
                        {notificationItems.map(({ notification }, index) => {

                          const { title, description, critical, read, id, revoked, timestamp } = notification;
                          const position = (idx: number) => {
                            const lastItem = idx === notificationItems.length - 1;
                            if (!lastItem && idx > 0) {
                              return "";
                            }
                            return !lastItem ? "firstItem" : "lastItem";
                          };

                          const truncatedDescription = getTruncatedDescription(description, critical, revoked);

                          return (
                            <NotificationBody key={id} $position={position(index)}>
                              <Stack spacing="mini" className="notification-message">
                                <Shelf alignment="center" className="notification-message-header">
                                  {!read ? (
                                    <>
                                      <Body size="mini">
                                        <Semibold>{timestamp}</Semibold>
                                      </Body>
                                      <NotificationUnreadIndicator />
                                    </>
                                  ) : (
                                    <Body size="mini">{timestamp}</Body>
                                  )}
                                </Shelf>
                                {critical && !revoked && (
                                  <Shelf spacing="mini" alignment="center" style={{ justifyContent: "flex-start" }}>
                                    <Tag type="error-high-contrast">Critical</Tag>
                                    {!read &&
                                      <Tag type="system-high-contrast">Acknowledgement Required</Tag>
                                    }
                                  </Shelf>
                                )}
                                {revoked ?
                                  <>
                                    <Shelf spacing="mini" alignment="center" style={{ justifyContent: "flex-start" }}>
                                      <Tag type="info">Revoked</Tag>
                                    </Shelf>
                                    <Stack>
                                      <Semibold>Revoked Notification Warning</Semibold>
                                    </Stack>
                                    <Stack spacing="mini">
                                      <Regular>
                                        A notification you previously received titled <Semibold>{notification?.title}</Semibold> has been revoked and no action
                                        is required on your behalf. For questions reach out to <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
                                      </Regular>
                                      <Shelf spacing="mini">
                                        <Semibold>Reason:</Semibold>
                                        <Regular>{description}</Regular>
                                        <Shelf.Spacer />
                                      </Shelf>
                                    </Stack>
                                  </>
                                : <>
                                  <Semibold>{title}</Semibold>
                                  <Paragraph>{truncatedDescription}</Paragraph>
                                </>
                                }
                                <Shelf spacing="small">
                                  {!read && !revoked && !critical && (
                                    <Shelf spacing="mini" alignment="center" style={{ justifyContent: "flex-start" }}>
                                      <a onClick={() => markAsReadHandler(notification)}>{"Mark As Read"}</a>
                                    </Shelf>
                                  )}
                                  <Shelf spacing="mini" alignment="center" style={{ justifyContent: "flex-start" }}>
                                    <a onClick={() => openNotificationHandler(notification)}>Open</a>
                                  </Shelf>
                                  <Shelf.Spacer />
                                </Shelf>
                              </Stack>
                            </NotificationBody>
                          );
                        })}
                      </Stack>
                    </NotificationScrollAreaInner>
                  </NotificationScrollAreaOuter>
                )}
              </Stack>
            </NotificationMenuBody>
          </MenuButton.ComponentItem>
        </MenuButton>
      </NotificationButtonContainer>
    </div>
  );
};
